/**
 * Path-based locale helpers. English lives at the root (no prefix); other
 * languages are prefixed: /es, /es/faith, /de/knowledge/slug, … The locale
 * home has no trailing slash (e.g. /es), matching the sitemap + canonical.
 */

export const SUPPORTED_LANGS = ["en", "es", "pt", "de", "zh"];

export const normalizeLang = (lng) => {
  const l = String(lng || "en").slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(l) ? l : "en";
};

/**
 * Split a router pathname into { lang, path } where `path` is the locale-less
 * logical path (always starts with "/"). `/es/faith` → { lang:"es", path:"/faith" }.
 */
export const stripLocale = (pathname) => {
  const segs = String(pathname || "/").split("/");
  const maybe = segs[1];
  if (SUPPORTED_LANGS.includes(maybe) && maybe !== "en") {
    const rest = "/" + segs.slice(2).join("/");
    return { lang: maybe, path: rest === "/" ? "/" : rest.replace(/\/+$/, "") || "/" };
  }
  return { lang: "en", path: pathname || "/" };
};

/**
 * Prefix a logical path with the locale (en = unchanged). Preserves any
 * ?query / #hash, leaves external / hash-only / mailto links untouched, and
 * never double-prefixes.
 */
export const localizePath = (to, lng) => {
  if (typeof to !== "string") return to;
  if (/^https?:\/\//.test(to) || to.startsWith("mailto:") || to.startsWith("#")) return to;

  const lang = normalizeLang(lng);
  // Separate the path from a trailing ?query / #hash.
  const splitIdx = to.search(/[?#]/);
  const pathPart = splitIdx === -1 ? to : to.slice(0, splitIdx);
  const suffix = splitIdx === -1 ? "" : to.slice(splitIdx);
  const cleanPath = pathPart.startsWith("/") ? pathPart : `/${pathPart}`;

  // Already locale-prefixed? leave it.
  const firstSeg = cleanPath.split("/")[1];
  if (SUPPORTED_LANGS.includes(firstSeg)) return to;

  if (lang === "en") return `${cleanPath}${suffix}`;
  const prefixed = cleanPath === "/" ? `/${lang}` : `/${lang}${cleanPath}`;
  return `${prefixed}${suffix}`;
};
